import React from 'react';
import { Button, Drawer, Flex, Form, Input } from 'antd';

import { useDispatch, useSelector } from 'react-redux';
import classes from './Activate.module.css';
import { useIntl } from 'react-intl';
import FormItem from 'antd/es/form/FormItem';
import { getAccountCards, setCards } from 'shared/config/store/reducers/cardsSlice';
import { Account } from 'entities/account/model/types';
import { AppDispatch, RootState } from 'shared/config/store/store';
import { getCurrentEmi } from 'shared/config/store/selectors/app';
import { getEMICurrentAccountObject } from 'shared/config/store/reducers/accountsSlice';
import { Card } from 'entities/card/model/types';
import { useSendMutation } from '../api';
import { setActivity } from 'shared/config/store/reducers/appSlice';
import { useMessage } from 'shared/libs/hooks/useMessage/useMessage';
import { ApiError } from 'shared/model/types';
import { activateCardSchema } from '../model/schema';
import { ActivateCardForm } from '../model/types';

type ActivateProps = {
  open: boolean;
  onClose: () => unknown;
};

const ActivateDrawer: React.FC<ActivateProps> = ({ open, onClose }) => {
  const { formatMessage: t } = useIntl();
  const { showSuccess, showError } = useMessage();
  const dispatch = useDispatch<AppDispatch>();

  const currentEmi = useSelector(getCurrentEmi);
  const currentAccount: Account = useSelector((state: RootState) =>
    getEMICurrentAccountObject(state, currentEmi)
  ) as Account;
  const card: Card = useSelector(getAccountCards(currentEmi, currentAccount.id))?.[0] as Card;

  const [send] = useSendMutation();

  const [form] = Form.useForm();
  const validationScheme = activateCardSchema(t);

  const onSubmit = (data: ActivateCardForm) => {
    dispatch(setActivity(true));
    send({
      accountId: currentAccount.id,
      cardId: card.id,
      cvv: data.cvv
    })
      .unwrap()
      .then(({ response }) => {
        dispatch(
          setCards({
            accountId: currentAccount.id,
            cards: [response],
            emi: currentEmi
          })
        );
        showSuccess(t({ id: 'card.activate.messages.success' }));
        onClose();
      })
      .catch((err: ApiError) => {
        console.log(err.data.errors?.[0]);
        if (err.data.errors?.[0].id === 'river.api.error') {
          showError(t({ id: 'card.activate.messages.wrongCvv' }));
        } else {
          showError(t({ id: 'card.activate.messages.failed' }));
        }
      })
      .finally(() => {
        dispatch(setActivity(false));
      });
  };

  return (
    <Drawer
      title={t({ id: 'card.application.activate' })}
      open={open}
      onClose={onClose}
      width={560}
      destroyOnClose>
      <Flex vertical justify='center' align='center' gap={16}>
        <Form form={form} onFinish={onSubmit} validateTrigger='onChange'>
          <FormItem rules={validationScheme.cvv} name='cvv'>
            <Input placeholder='CVV' />
          </FormItem>
        </Form>
        <Button type='primary' className={classes.button} onClick={form.submit}>
          {t({ id: 'card.application.activate' })}
        </Button>
      </Flex>
    </Drawer>
  );
};

export default ActivateDrawer;
